/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useDeleteConversation } from '@/hooks/useChat';
import {
  getConversationMode,
  getConversationModes,
  resolveSelectableMode,
} from '@/sections/insights/aiAssistant/config/storage';
import { useAutoDrawerStore } from '@/sections/insights/aiAssistant/stores/autoDrawer.store';
import type {
  AIAssistantMode,
  SidebarTab,
} from '@/sections/insights/aiAssistant/types';
import { extractConversationId } from '@/sections/insights/aiAssistant/utils/conversationId';

interface UseConversationManagerProps {
  conversations: any[];
  messages: any[];
  conversationId: string | null;
  setMode: (mode: AIAssistantMode) => void;
  setActiveTab: (tab: SidebarTab) => void;
  setActiveAgent: (agent: string | null) => void;
  /** Optional: called by handleCreateConversation to clear waiting state. */
  setIsWaitingForResponse?: (waiting: boolean) => void;
  isInputDisabled: boolean;
}

export function useConversationManager({
  conversations,
  messages,
  conversationId,
  setMode,
  setActiveTab,
  setActiveAgent,
  setIsWaitingForResponse,
  isInputDisabled,
}: UseConversationManagerProps) {
  const [, setSearchParams] = useSearchParams();
  const [deletedConversationIds, setDeletedConversationIds] = useState<
    Set<string>
  >(new Set());

  // modeChangeRef is owned here so handleModeChange and useConversationSwitch
  // can share state. It is returned and threaded into useConversationSwitch.
  const modeChangeRef = useRef<'auto' | null>(null);

  const resetAutoDrawer = useAutoDrawerStore(s => s.reset);

  const deleteConversation = useDeleteConversation();

  const handleModeChange = (newMode: AIAssistantMode) => {
    setMode(newMode);
    if (newMode === 'auto') {
      setActiveTab('hunts');
      modeChangeRef.current = 'auto';
      resetAutoDrawer();
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('conversationId');
        return newParams;
      });
    } else {
      setActiveTab('conversations');
      modeChangeRef.current = null;
      resetAutoDrawer();
    }
  };

  const handleCreateConversation = () => {
    if (isInputDisabled) return;

    setIsWaitingForResponse?.(false);
    setMode('query');
    setActiveAgent(null);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('conversationId');
      return newParams;
    });
  };

  const handleSelectConversation = (id: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('conversationId', id);
      return newParams;
    });

    // A selected sidebar conversation is always a chat conversation: hunt
    // conversations are excluded from the list by isSidebarConversation
    // (useChatWorker), so we never need to route a selection into the hunt
    // section here. A stale 'auto' saved mode heals to 'query'.
    const saved = getConversationMode(id);
    setMode(resolveSelectableMode(saved));
    useAutoDrawerStore.getState().reset();
  };

  const handleDeleteConversation = (id: string) => {
    // Optimistically hide it from the list immediately
    setDeletedConversationIds(prev => new Set(prev).add(id));

    deleteConversation.mutate(id, {
      onSuccess: () => {
        if (conversationId === id) {
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('conversationId');
            return newParams;
          });
        }
      },
      onError: () => {
        // Rollback if the API call fails
        setDeletedConversationIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
    });
  };

  // Filter out optimistically deleted conversations
  const visibleConversations = useMemo(
    () =>
      conversations.filter((c: any) => {
        const id = extractConversationId(c);
        return !deletedConversationIds.has(id);
      }),
    [conversations, deletedConversationIds]
  );

  // Pre-compute lightweight preview map for ConversationList (prevents DataCloneError)
  const conversationPreviews = useMemo(() => {
    const previews: Record<string, string> = {};
    for (const conv of conversations) {
      const id = extractConversationId(conv);
      // Already has a title from the backend? Skip message scanning.
      if (conv.name || conv.title || conv.topic) continue;

      // Find first user message for this conversation
      const firstUserMsg = messages.find(
        (msg: any) =>
          msg.role === 'user' &&
          (msg.key?.includes(id) || msg.conversationId === id)
      );
      if (firstUserMsg?.content) {
        const words = firstUserMsg.content
          .trim()
          .split(' ')
          .slice(0, 6)
          .join(' ');
        previews[id] =
          words.length > 40 ? words.substring(0, 40) + '...' : words;
      }
    }
    return previews;
  }, [conversations, messages]);

  const modeFilteredConversations = useMemo(() => {
    const allModes = getConversationModes(); // read localStorage once per render
    return visibleConversations.filter((conv: any) => {
      const convId = extractConversationId(conv);
      const savedMode = allModes[convId]?.mode ?? 'query';
      return savedMode !== 'auto';
    });
  }, [visibleConversations]);

  return {
    visibleConversations,
    conversationPreviews,
    modeFilteredConversations,
    handleDeleteConversation,
    handleModeChange,
    handleCreateConversation,
    handleSelectConversation,
    modeChangeRef,
  };
}
